import React, { useState } from "react";
import { X } from "lucide-react";

import { getApiUrl, FEEDBACK_API_PATH } from "../utils/api";

/**
 * Self-contained feedback popup. session_id / username / first_prompt are
 * attached silently in the background — the agent only ever sees the
 * questions below, never that this context is being sent.
 *
 * Posts to the feedback endpoint (code-isolated in server/feedback_service,
 * but mounted into the main backend — same origin, no separate process).
 */

const RADIO_QUESTIONS = [
  {
    key: "resolved_in_appropriate_steps",
    question: "Were you able to resolve your case in appropriate number of steps?",
    options: ["Yes", "No", "Yes but too many steps"],
  },
  {
    key: "guidance_understood",
    question: "Was the diagnostic guidance in terms of Summary and Logic of reasoning understood?",
    options: ["Yes, the summary with Citation and questions were understood", "Sometimes", "No"],
  },
  {
    key: "citation_helpful",
    question:
      "Was the Citation Source text helpful in showing diagnostic steps grounded in the source documents (No hallucination)?",
    options: ["Yes", "Sometime", "No"],
  },
  {
    key: "usage_preference",
    question: "When would you use these Diagnostics Step (interactive or batch mode)?",
    options: [
      "Always. Should be enabled through Resolver",
      "Only on escalations. Resolvr to decide when to use.",
      "No, this may never be used in our workflow",
    ],
  },
];

const TEXT_QUESTIONS = [
  {
    key: "search_vs_diagnostic_difference",
    question: "Were you able to see the difference between Search and Diagnostic analysis means of resolving Issues?",
  },
  {
    key: "feature_to_develop",
    question: "What feature would you like see most developed?",
  },
  {
    key: "additional_feedback",
    question: "Feel free to let us know your feedback not covered so far",
  },
];

const EMPTY_ANSWERS = {
  ...Object.fromEntries([...RADIO_QUESTIONS, ...TEXT_QUESTIONS].map((q) => [q.key, ""])),
  overall_rating: null,
};

const RadioQuestion = ({ question, options, value, onChange }) => (
  <div className="mb-4">
    <p className="text-sm font-medium text-[#253439] mb-2">{question}</p>
    <div className="flex flex-col gap-1.5">
      {options.map((opt) => (
        <label
          key={opt}
          className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition ${
            value === opt ? "border-cyan-400 bg-cyan-50 text-cyan-900" : "border-gray-200 text-[#253439] hover:bg-gray-50"
          }`}
        >
          <input
            type="radio"
            className="mt-0.5 accent-cyan-500"
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  </div>
);

const RatingQuestion = ({ question, value, onChange }) => (
  <div className="mb-4">
    <p className="text-sm font-medium text-[#253439] mb-1">{question}</p>
    <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
      <span>1 — Not useful</span>
      <span>5 — Most useful</span>
    </div>
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`flex-1 py-2 rounded-lg border text-sm font-semibold transition ${
            value === n
              ? "bg-cyan-500 border-cyan-500 text-white"
              : "border-gray-200 text-gray-500 hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700"
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

const TextQuestion = ({ question, value, onChange }) => (
  <div className="mb-4">
    <p className="text-sm font-medium text-[#253439] mb-2">{question}</p>
    <textarea
      className="w-full resize-none rounded-lg border border-gray-200 bg-white p-2.5 text-sm text-[#253439] placeholder-gray-400 outline-none transition-colors focus:border-cyan-300"
      style={{ minHeight: "76px" }}
      placeholder="Type your answer..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default function FeedbackModal({ sessionId, username, firstPrompt, surface, onClose }) {
  const [answers, setAnswers] = useState(EMPTY_ANSWERS);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const requiredAnswered = RADIO_QUESTIONS.every((q) => answers[q.key]) && answers.overall_rating != null;

  const submit = async () => {
    if (!requiredAnswered) return;
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(getApiUrl(FEEDBACK_API_PATH), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId || null,
          username: username || null,
          first_prompt: firstPrompt || null,
          // "engineer" | "user". The modal is the same on both surfaces, so
          // the row it writes is indistinguishable without this -- and the
          // feedback service cannot work it out for itself: it imports nothing
          // from the main app by design, so it cannot read the session.
          surface: surface || null,
          ...answers,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Server error ${res.status}`);
      }
      setStatus("sent");
      setTimeout(() => onClose?.(), 1200);
    } catch (err) {
      setStatus("error");
      setError(err.message || "Could not submit feedback. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 px-4 py-10 backdrop-blur-[2px]"
      onClick={onClose}
    >
      {/* Three rows, not one scroll box: header and footer are siblings OF the
          scroll region rather than sticky children INSIDE it. The old layout
          scrolled the whole card and pinned the buttons with `sticky bottom-0`
          plus negative margins to escape the padding — which left the question
          text sliding through the strip beside and beneath the bar. A footer
          that is outside the scrollport cannot be scrolled behind.
          Shell matches AllChatsModal: same backdrop, radius, shadow and
          gray-100 dividers. */}
      <div
        className="flex max-h-full w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        {status === "sent" ? (
          <div className="px-6 py-10 text-center">
            <div className="text-sm font-semibold text-cyan-600">Thanks for your feedback!</div>
          </div>
        ) : (
          <>
            {/* Row 1 — fixed. */}
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-[15px] font-semibold text-[#253439]">Share your feedback</h3>
                <p className="mt-0.5 text-xs text-gray-500">Help us improve the diagnostic experience.</p>
              </div>
              <button
                onClick={onClose}
                type="button"
                aria-label="Close"
                className="-mr-1 flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <X size={16} />
              </button>
            </div>

            {/* Row 2 — the only thing that scrolls. */}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {firstPrompt && (
              <div className="mb-4 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">Your question</div>
                <div className="text-sm text-[#253439] line-clamp-3">{firstPrompt}</div>
              </div>
            )}

            {RADIO_QUESTIONS.map((q) => (
              <RadioQuestion key={q.key} question={q.question} options={q.options} value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)} />
            ))}

            <RatingQuestion
              question="Overall, how useful was the Diagnostic Agent?"
              value={answers.overall_rating}
              onChange={(v) => setAnswer("overall_rating", v)}
            />

            <p className="text-xs text-gray-400 mb-2">Optional questions below, if you have a moment...</p>

            {TEXT_QUESTIONS.map((q) => (
              <TextQuestion key={q.key} question={q.question} value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)} />
            ))}

            </div>

            {/* Row 3 — fixed. Validation lives here rather than at the foot of
                the scroll region: it explains why Submit is dead, so it has to
                be readable at the moment the pointer is over Submit — not two
                screens up. */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3.5">
              {status === "error" && (
                <span className="mr-auto text-xs text-red-500">{error}</span>
              )}
              {status !== "error" && !requiredAnswered && (
                <span className="mr-auto text-xs text-gray-400">
                  Answer the choice and rating questions to submit.
                </span>
              )}
              <button
                onClick={onClose}
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
                disabled={status === "sending"}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                type="button"
                disabled={!requiredAnswered || status === "sending"}
                className="rounded-lg bg-cyan-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
              >
                {status === "sending" ? "Submitting..." : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
